<template>
  <div class="container">
    <md-card class="dash-content">
      <div class="header-wrapper">
        <PageHeader title="Manage Handyman" />
        <div class="loader">
          <Wave
            v-if="loading || updating || deleting"
            width="50px"
            color="#d9534f"
          />
        </div>
      </div>
      <md-table
        v-model="handymen.mdData"
        md-card
        md-sort="title"
        md-sort-order="asc"
      >
        <md-table-toolbar class="mb-4">
          <div class="md-toolbar-section-start">
            <md-button
              class="md-primary button-icon"
              :disabled="exporting"
              @click="exportExcel"
            >
              Download Excel
              <img src="@/assets/svg/excel.svg" />
            </md-button>
          </div>
          <div class="toolbar-filter-wrapper">
            <md-field md-clearable class="md-toolbar-section-end">
              <md-input placeholder="Search by name..." v-model="search" />
            </md-field>
            <md-menu md-size="medium" md-align-trigger>
              <md-button
                md-menu-trigger
                class="md-primary button-icon"
                disabled
              >
                Filter
                <img src="@/assets/svg/filter.svg" />
              </md-button>

              <md-menu-content>
                <md-menu-item>Approved</md-menu-item>
                <md-menu-item>Pending</md-menu-item>
              </md-menu-content>
            </md-menu>
          </div>
        </md-table-toolbar>
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item }"
          :class="{ 'table-highlight': item.promote_count > 0 }"
        >
          <md-table-cell md-label="S/N" md-numeric>{{ item.id }}</md-table-cell>
          <md-table-cell md-label="Name">{{
            item.user ? item.user.name : "--"
          }}</md-table-cell>
          <md-table-cell md-label="Email">{{
            item.user ? item.user.email : "--"
          }}</md-table-cell>
          <md-table-cell md-label="Phone number"
            >{{ item.phone_ext }}{{ item.phone }}</md-table-cell
          >
          <md-table-cell md-label="Company">{{
            item.company_name
          }}</md-table-cell>
          <md-table-cell md-label="Location">
            {{ item.state ? item.state.title : "--" }}
          </md-table-cell>
          <md-table-cell md-label="Services">
            <div class="py-2">
              <Badge
                v-for="service in item.services"
                :key="service.id"
                :text="service.title"
                variant="grey"
              />
            </div>
          </md-table-cell>
          <!-- <md-table-cell md-label="code">{{
            item.tracking_code
          }}</md-table-cell> -->

          <md-table-cell md-label="Registered By">
            <span class="badge yellow">
              {{
                item.register
                  ? item.register.user.first_name +
                    " " +
                    item.register.user.last_name
                  : "User"
              }}
            </span>
          </md-table-cell>
          <md-table-cell md-label="Status">
            <div
              class="badge"
              :class="{
                success: item.approval_status == 'approved',
                danger: item.approval_status == 'disapproved'
              }"
            >
              {{ item.approval_status }}
            </div>
          </md-table-cell>
          <md-table-cell md-label="Actions">
            <md-menu md-size="medium" md-align-trigger>
              <md-button :disabled="updating" md-menu-trigger class="bggreen"
                >Menu</md-button
              >

              <md-menu-content>
                <md-menu-item @click="setHandyman(item)">Preview</md-menu-item>
                <div>
                  <md-menu-item
                    v-if="
                      item.promote_count == 0 &&
                      permission(permissions.PROMOTE_HANDYMAN)
                    "
                    @click="promote(item)"
                    >Promote <i class="material-icons">arrow_upward</i>
                  </md-menu-item>
                  <md-menu-item
                    v-else-if="
                      item.promote_count > 0 &&
                      permission(permissions.PROMOTE_HANDYMAN)
                    "
                    @click="promote(item, 'update')"
                    >Update Promotion
                  </md-menu-item>
                  <md-menu-item
                    v-if="
                      ['disapproved', 'pending'].includes(
                        item.approval_status
                      ) && permission(permissions.APPROVE_HANDYMAN)
                    "
                    @click="changeHandymanStatus(item.id, 'approved')"
                    >Approve</md-menu-item
                  >
                  <md-menu-item
                    v-if="
                      ['approved', 'pending'].includes(item.approval_status) &&
                      permission(permissions.APPROVE_HANDYMAN)
                    "
                    @click="changeHandymanStatus(item.id, 'disapproved')"
                    >Disapprove</md-menu-item
                  >
                  <md-menu-item
                    v-if="permission(permissions.VIEW_HANDYMAN_SERVICE)"
                    @click="setHandyman(item, 'services')"
                    >Services</md-menu-item
                  >
                  <md-menu-item
                    v-if="permission(permissions.DELETE_HANDYMAN)"
                    @click="remove(item.id)"
                    >Delete
                  </md-menu-item>
                </div>
              </md-menu-content>
            </md-menu>
          </md-table-cell>
        </md-table-row>
      </md-table>
      <span v-if="loading" class="b-loading">Loading...</span>
      <pagination
        v-model="handymen.mdPage"
        :records="handymen.mdCount"
        :per-page="handymen.mdPerPage"
        @paginate="updatePagination"
      />
    </md-card>

    <md-dialog :md-active.sync="show">
      <md-dialog-title>Handyman Details</md-dialog-title>
      <div class="cancel-modal">
        <i class="material-icons" @click="show = false">close</i>
      </div>

      <div class="modal-contain" v-if="handyman">
        <div class="item">
          <strong>Company name</strong>
          <span> {{ handyman.company_name }}</span>
        </div>
        <div class="item">
          <strong>About</strong>
          <span v-html="handyman.about"> </span>
        </div>

        <div class="item">
          <strong>Services</strong>
          <span
            class="badge"
            v-for="service in handyman.services"
            :key="service.id"
          >
            {{ service.title }}
          </span>
        </div>
        <div class="item">
          <strong>Rating</strong>
          <span> {{ handyman.rating }}</span>
        </div>

        <div class="item">
          <strong>Phone Number</strong>
          <a :href="`tel:${handyman.phone_ext + handyman.phone}`">
            {{ handyman.phone_ext + handyman.phone }}</a
          >
        </div>

        <div class="item">
          <strong>Date Registered</strong>
          <span> {{ handyman.created_at | formatDate }}</span>
        </div>

        <div class="item">
          <strong>Approval Status</strong>
          <div
            class="badge"
            :class="{
              success: handyman.approval_status == 'approved',
              danger: handyman.approval_status == 'disapproved'
            }"
          >
            {{ handyman.approval_status }}
          </div>
        </div>

        <div class="item">
          <strong>Tracking code</strong>
          <span> {{ handyman.tracking_code }}</span>
        </div>

        <div class="item">
          <strong>Logo</strong>
        </div>
        <div class="media">
          <div class="logo">
            <img
              :src="getStorageUrl(handyman.logo)"
              onerror="this.src='https://via.placeholder.com/220x150?text=No%20Image'"
              alt="Display Image"
            />
          </div>
        </div>

        <div class="item">
          <strong>Tin</strong>
          <span> {{ handyman.tin }}</span>
        </div>

        <div class="media">
          <div class="doc-display">
            <img
              :src="handyman.cac_reg_upload"
              onerror="this.src='https://via.placeholder.com/220x150?text=No%20Image'"
              alt="Display Image"
            />
          </div>
        </div>

        <hr />
        <div>
          <h4>Socials information</h4>
        </div>

        <div class="item">
          <strong>Facebook</strong>
          <span> {{ handyman.facebook_link }}</span>
        </div>

        <div class="item">
          <strong>Linkedin</strong>
          <span> {{ handyman.linkedin_link }}</span>
        </div>
        <div class="item">
          <strong>Instagram</strong>
          <span> {{ handyman.instagram_link }}</span>
        </div>

        <hr />
        <div>
          <h4>User information</h4>
        </div>
        <div class="item">
          <strong>Fullname</strong>
          <span> {{ handyman.user.name }}</span>
        </div>

        <div class="item">
          <strong>Email Address</strong>
          <span> {{ handyman.user.email }}</span>
        </div>

        <div class="item">
          <strong>Phone Number</strong>
          <a :href="`tel:${handyman.user.phone_ext + handyman.user.phone}`">
            {{ handyman.user.phone_ext + handyman.user.phone }}</a
          >
        </div>
      </div>
    </md-dialog>

    <md-dialog :md-active.sync="showS">
      <md-dialog-title>Handyman Services</md-dialog-title>
      <div class="cancel-modal">
        <i class="material-icons" @click="showS = false">close</i>
      </div>

      <div class="modal-contain" v-if="handyman">
        <div class="s-con" v-if="handyman.services.length">
          <div class="head">
            <div>
              <md-checkbox v-model="checkAllServices" @change="toggleServices">
              </md-checkbox>
            </div>
            <div>Title</div>
            <div>Status</div>
          </div>
          <div class="body">
            <div
              class="s-items"
              v-for="service in handyman.services"
              :key="service.id"
            >
              <div class="s-item">
                <md-checkbox v-model="multi_services" :value="service.pivot.id">
                </md-checkbox>
              </div>
              <div class="s-item">{{ service.title }}</div>
              <div class="s-item">{{ service.pivot.approval_status }}</div>
            </div>
          </div>

          <select
            class="form-control"
            name="approval_status"
            v-model="approval_status"
          >
            <option value="" disabled>Select Approval Status</option>
            <option value="approved">Approve</option>
            <option value="pending">Pending</option>
            <option value="disapproved">Disapproved</option>
          </select>
          <br />

          <mdc-button
            :disabled="!approval_status"
            :loading="updating"
            v-on:click.native="submitServices"
          >
            {{ "Update Service" }}</mdc-button
          >
        </div>
        <div v-else>
          <div class="alert alert-info">
            <span>Handyman has no service</span>
          </div>
        </div>
      </div>
    </md-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import Badge from "@/components/Badge.vue";
const { mapActions, mapGetters } = createNamespacedHelpers("handyman");
import { permissions } from "@/constants";

export default {
  name: "handyman",
  data() {
    return {
      updating: false,
      deleting: false,
      exporting: false,
      search: "",
      show: false,
      time: null,
      handyman: null,
      showS: false,
      multi_services: [],
      checkAllServices: false,
      approval_status: "",
      permissions
    };
  },
  watch: {
    search(val) {
      this.waitForIt(val);
    },
    handyman(val) {
      let data = [];
      this.multi_services = [];
      this.checkAllServices = false;

      // val.services.map((service) => {
      //   if (service.pivot.approval_status == "approved") {
      //     data.push(service.pivot.id);
      //   }
      // });

      // this.multi_services = data;
    }
  },

  methods: {
    ...mapActions([
      "getHandymen",
      "updateHandymanStatus",
      "updateServiceStatus",
      "deleteHandyman"
    ]),
    async updatePagination(page) {
      let data = { page };
      if (this.search) data.keyword = this.search;
      await this.getHandymen(data);
    },
    async changeHandymanStatus(id, status) {
      try {
        this.updating = true;
        await this.updateHandymanStatus({ approval_status: status, id });
        this.updating = false;
        this.toast("success", "Handyman Status Updated ");
      } catch (err) {
        this.updating = false;
        console.error(err);
      }
    },
    async exportExcel() {
      this.exporting = true;
      await this.$store.dispatch("auth/export", { model: "handyman" });
      this.exporting = false;
    },
    async submitSearch() {
      await this.getHandymen({ keyword: this.search });
    },
    async submitServices() {
      this.updating = true;
      var data = new FormData();
      if (this.approval_status == "") return;
      data.append("approval_status", this.approval_status);
      data.append("services", this.multi_services);
      data.append("_method", "PATCH");

      await this.updateServiceStatus(data);
      this.updating = false;
      this.showS = false;
      this.toast("success", "Status updated");
    },
    async promote(item, mode = "create") {
      this.$eventBus.$emit("promote", {
        item_type: this.promotions.HANDYMAN,
        item_id: item.id,
        title: item.company_name,
        promote: item.promote,
        mode
      });
    },
    setHandyman(handyman, type = "handyman") {
      if (type == "handyman") {
        this.show = true;
      } else {
        this.showS = true;
      }
      this.handyman = handyman;
    },
    waitForIt(val) {
      if (this.time) {
        clearTimeout(this.time);
      }
      this.time = setTimeout(() => this.submitSearch(val), 1000);
    },
    toggleServices: function () {
      if (this.checkAllServices) {
        this.multi_services = [];
        this.multi_services = this.handyman.services.map(
          (service) => service.pivot.id
        );
      } else {
        this.multi_services = [];
      }
    },
    async remove(id) {
      const conf = confirm("Are you sure you want to delete this handyman?");
      if (conf) {
        try {
          this.deleting = true;
          await this.deleteHandyman(id);
          this.deleting = false;
          this.toast("success", "Handyman deleted");
        } catch (err) {
          this.deleting = false;
          this.toast("error", "Unable to delete handyman");
        }
      }
    }
  },
  created() {
    this.$eventBus.$on("promoted", (id) => {
      this.getHandymen();
    });
  },
  mounted() {
    this.getHandymen();
  },
  computed: {
    ...mapGetters(["handymen", "loading"]),
    hasPermission() {
      return this.$store.getters["auth/has_module_permission"]("crud");
    },
    permission() {
      return this.$store.getters["auth/has_module_permission"];
    }
  },
  components: {
    Badge
  }
};
</script>

<style lang="scss" scoped>
.md-table-cell {
  width: 0% !important;
  padding: 0rem 0.8rem;
}

.md-table + .md-table {
  margin-top: 16px;

  thead {
    height: 50px !important;
  }
}

.badge {
  background: #f3f3f3;

  &.success {
    background: green;
  }

  &.danger {
    background: red;
  }
}

.s-con {
  .head,
  .body .s-items {
    display: flex;
    align-items: center;

    > div {
      width: calc(100% / 3);
    }
  }

  .head {
    font-weight: 700;
  }

  .body .s-items .s-item {
    padding: 10px 0px;
  }
}
</style>
